import { CloudUploadOutlined } from '@ant-design/icons';
import { App, Button, Upload } from 'antd';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNewsClassNames } from './News.styles.ts';
import type { AttachmentNumber } from './News.tsx';

export type UploadResult = {
  attachmentId: string;
  size: number;
  filename: string;
};

export const UploadButton: React.FC<{
  labId: string;
  number: AttachmentNumber;
  onSuccess: (result: UploadResult) => void;
}> = ({ labId, number, onSuccess }) => {
  const { message } = App.useApp();
  const auth = useAuth();
  const [uploading, setUploading] = useState(false);
  const classNames = useNewsClassNames();

  return (
    <Upload
      showUploadList={false}
      maxCount={1}
      className={classNames.uploadButton}
      beforeUpload={file => {
        return new Promise((resolve, reject) => {
          if (file.size > 8000000 /* 8MB */) {
            message.error('Der Anhang darf nicht größer als 8MB sein');
            reject();
            return;
          }

          if (file.type !== 'application/pdf') {
            message.error('Der Anhang ist keine PDF-Datei, nur PDFs sind erlaubt');
            reject();
            return;
          }

          resolve();
        });
      }}
      customRequest={async info => {
        setUploading(true);
        const endpoint = `${window._env_.API_URL}/rest/news-attachment/upload/${labId}/${number}`;
        const formData = new FormData();
        formData.append('file', info.file);

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
            headers: { authorization: `Bearer ${auth.user?.access_token}` },
          });

          if (!response.ok) {
            message.error('Beim Hochladen des Anhangs ist ein Fehler aufgetreten');
            return;
          }

          const attachmentId = await response.text();

          const file = info.file as File;
          onSuccess({ attachmentId, size: file.size, filename: file.name });
          message.success('Anhang wurde erfolgreich hochgeladen');
        } catch {
          message.error('Beim Hochladen des Anhangs ist ein Fehler aufgetreten');
        } finally {
          setUploading(false);
        }
      }}
    >
      <Button icon={<CloudUploadOutlined />} loading={uploading} className={classNames.w100}>
        PDF hochladen
      </Button>
    </Upload>
  );
};
